import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:5001' : '');

const JURISDICTION_LEVELS = [
  { value: '', label: 'All jurisdictions' },
  { value: 'city', label: 'City' },
  { value: 'county', label: 'County' },
  { value: 'state', label: 'State' },
  { value: 'federal', label: 'Federal' },
  { value: 'agency', label: 'Agency' }
];

function RegulationAnalysis() {
  const navigate = useNavigate();
  const { uuid: urlUuid } = useParams();
  const [analyses, setAnalyses] = useState([]);
  const [folders, setFolders] = useState([]);
  const [filter, setFilter] = useState('');
  const [folderFilter, setFolderFilter] = useState(''); // '' = all, 'unfiled' = no folder, or folder uuid
  const [active, setActive] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(new Set()); // selected uuids for comparison
  const [comparison, setComparison] = useState(null);
  const [comparing, setComparing] = useState(false);
  const pollRef = useRef(null);

  // Upload form state
  const [tab, setTab] = useState('url'); // 'url' | 'text' | 'file'
  const [url, setUrl] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [jurisdictionLevel, setJurisdictionLevel] = useState('city');
  const [jurisdictionName, setJurisdictionName] = useState('');
  const [enforcementAgency, setEnforcementAgency] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchList = useCallback(async () => {
    try {
      const qs = filter ? `?jurisdiction_level=${encodeURIComponent(filter)}` : '';
      const res = await fetch(`${API_URL}/api/regulation-analyses${qs}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setAnalyses(data.analyses || []);
    } catch (e) { console.error('list error', e); }
  }, [filter]);

  const fetchFolders = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/regulation-folders`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setFolders(data.folders || []);
    } catch (e) { /* ignore — endpoint may be unauth */ }
  }, []);

  const handleNewFolder = async () => {
    const name = window.prompt('Folder name?');
    if (!name?.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/regulation-folders`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim() })
      });
      const data = await res.json();
      if (data.success) fetchFolders();
      else alert(data.error || 'Failed');
    } catch (e) { alert(e.message); }
  };

  const handleMoveToFolder = async (uuid, folderUuid) => {
    try {
      await fetch(`${API_URL}/api/regulation-analysis/${uuid}/folder`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderUuid: folderUuid || null })
      });
      fetchList(); fetchFolders();
    } catch (e) { alert(e.message); }
  };

  const toggleSelect = (uuid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid); else next.add(uuid);
      return next;
    });
  };

  const handleCompare = async () => {
    if (selected.size < 2) { alert('Select at least 2 regulations'); return; }
    if (selected.size > 5) { alert('Max 5 regulations at a time'); return; }
    setComparing(true);
    setComparison(null);
    try {
      const res = await fetch(`${API_URL}/api/regulation-analysis/compare`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uuids: Array.from(selected) })
      });
      const data = await res.json();
      if (!data.success) { alert(data.error || 'Compare failed'); return; }
      setComparison(data);
      setActive(null);
      navigate('/regulation-analysis');
    } catch (e) { alert(e.message); }
    finally { setComparing(false); }
  };

  const loadDetail = useCallback(async (uuid) => {
    setLoadingDetail(true);
    try {
      const res = await fetch(`${API_URL}/api/regulation-analysis/${uuid}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) setActive(data.analysis);
      else setError(data.error || 'Failed to load');
    } catch (e) { setError(e.message); }
    finally { setLoadingDetail(false); }
  }, []);

  const startPolling = useCallback((uuid) => {
    if (pollRef.current) clearInterval(pollRef.current);
    const tick = async () => {
      try {
        const res = await fetch(`${API_URL}/api/regulation-analysis/${uuid}`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setActive(data.analysis);
          if (data.analysis.status === 'completed' || data.analysis.status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            fetchList();
          }
        }
      } catch (e) { /* keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 2500);
  }, [fetchList]);

  useEffect(() => { fetchList(); fetchFolders(); }, [fetchList, fetchFolders]);
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  // Deep-link load
  useEffect(() => {
    if (!urlUuid) { setActive(null); return; }
    if (active?.uuid === urlUuid) return;
    loadDetail(urlUuid).then(() => {});
  }, [urlUuid, active, loadDetail]);

  // Once active is loaded for a processing job, start polling
  useEffect(() => {
    if (active?.status === 'processing' && (!pollRef.current || active.uuid !== urlUuid)) {
      startPolling(active.uuid);
    }
  }, [active, urlUuid, startPolling]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      let res;
      if (tab === 'file') {
        if (!file) { setError('Pick a file'); setSubmitting(false); return; }
        const fd = new FormData();
        fd.append('file', file);
        fd.append('jurisdiction_level', jurisdictionLevel);
        fd.append('jurisdiction_name', jurisdictionName);
        fd.append('enforcement_agency', enforcementAgency);
        res = await fetch(`${API_URL}/api/regulation-analysis`, { method: 'POST', credentials: 'include', body: fd });
      } else {
        const body = {
          jurisdiction_level: jurisdictionLevel,
          jurisdiction_name: jurisdictionName,
          enforcement_agency: enforcementAgency
        };
        if (tab === 'url') body.url = url;
        else body.text = text;
        if ((tab === 'url' && !url.trim()) || (tab === 'text' && !text.trim())) {
          setError(`Provide a ${tab}`); setSubmitting(false); return;
        }
        res = await fetch(`${API_URL}/api/regulation-analysis`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
      }
      const data = await res.json();
      if (!data.success) { setError(data.error || 'Submit failed'); setSubmitting(false); return; }
      setUrl(''); setText(''); setFile(null);
      navigate(`/regulation-analysis/${data.uuid}`);
      fetchList();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReanalyze = async () => {
    if (!active) return;
    if (!window.confirm('Reanalyze this regulation? This will replace the current analysis.')) return;
    try {
      const res = await fetch(`${API_URL}/api/regulation-analysis/${active.uuid}/reanalyze`, {
        method: 'POST', credentials: 'include'
      });
      const data = await res.json();
      if (!data.success) { alert(data.error || 'Reanalyze failed'); return; }
      startPolling(active.uuid);
    } catch (err) { alert(err.message); }
  };

  const handleDelete = async () => {
    if (!active) return;
    if (!window.confirm('Delete this analysis?')) return;
    try {
      const res = await fetch(`${API_URL}/api/regulation-analysis/${active.uuid}`, {
        method: 'DELETE', credentials: 'include'
      });
      if (res.ok) {
        setActive(null);
        navigate('/regulation-analysis');
        fetchList();
      } else {
        alert('Delete failed');
      }
    } catch (err) { alert(err.message); }
  };

  return (
    <div style={{ display: 'flex', minHeight: 'calc(100vh - 80px)' }}>
      {/* Sidebar */}
      <aside style={{
        width: '340px', borderRight: '1px solid #e5e7eb', background: '#fafafa',
        padding: '20px', overflowY: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '15px', color: '#1a237e' }}>Saved Regulations</h3>
          <button onClick={handleNewFolder} title="New folder" style={{
            background: 'none', border: 'none', cursor: 'pointer', color: '#1565c0', fontSize: '18px', padding: 0
          }}>＋📁</button>
        </div>

        <select value={filter} onChange={e => setFilter(e.target.value)} style={{
          width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc',
          fontSize: '13px', marginBottom: '8px'
        }}>
          {JURISDICTION_LEVELS.map(j => <option key={j.value} value={j.value}>{j.label}</option>)}
        </select>

        {folders.length > 0 && (
          <select value={folderFilter} onChange={e => setFolderFilter(e.target.value)} style={{
            width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #ccc',
            fontSize: '13px', marginBottom: '8px'
          }}>
            <option value="">All folders</option>
            <option value="unfiled">Unfiled</option>
            {folders.map(f => (
              <option key={f.uuid} value={f.uuid}>{f.name} ({f.regulation_count})</option>
            ))}
          </select>
        )}

        {selected.size >= 2 && (
          <button onClick={handleCompare} disabled={comparing} style={{
            width: '100%', padding: '8px', borderRadius: '6px', border: 'none',
            background: comparing ? '#90caf9' : '#1565c0', color: 'white',
            fontSize: '13px', fontWeight: 600, cursor: comparing ? 'wait' : 'pointer',
            marginBottom: '12px'
          }}>{comparing ? 'Comparing…' : `Compare ${selected.size} jurisdictions`}</button>
        )}

        {(() => {
          const filtered = analyses.filter(a => {
            if (!folderFilter) return true;
            if (folderFilter === 'unfiled') return !a.folder_id;
            const f = folders.find(ff => ff.uuid === folderFilter);
            return f && a.folder_id === f.id;
          });
          if (filtered.length === 0) {
            return <p style={{ fontSize: '13px', color: '#999', fontStyle: 'italic' }}>No analyses match this filter.</p>;
          }
          return filtered.map(a => (
            <div key={a.uuid} style={{
              padding: '10px 12px', borderRadius: '8px',
              background: active?.uuid === a.uuid ? '#e3f2fd' : 'white',
              border: selected.has(a.uuid) ? '1.5px solid #1565c0' : '1px solid #e5e7eb',
              marginBottom: '8px', position: 'relative'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <input type="checkbox"
                  checked={selected.has(a.uuid)}
                  onChange={() => toggleSelect(a.uuid)}
                  onClick={e => e.stopPropagation()}
                  style={{ marginTop: '3px' }} />
                <div style={{ flex: 1, cursor: 'pointer' }}
                     onClick={() => navigate(`/regulation-analysis/${a.uuid}`)}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a237e', marginBottom: '4px' }}>
                    {a.title || 'Untitled regulation'}
                  </div>
                  <div style={{ fontSize: '11px', color: '#666' }}>
                    {a.jurisdiction_level && <span style={{ marginRight: '6px' }}>📍 {a.jurisdiction_level}</span>}
                    {a.jurisdiction_name && <span>{a.jurisdiction_name}</span>}
                  </div>
                  <div style={{ fontSize: '11px', color: a.status === 'failed' ? '#c62828' : '#999', marginTop: '4px' }}>
                    {a.status}{a.status === 'processing' && ` · ${a.progress || 0}%`}
                  </div>
                </div>
                {folders.length > 0 && (
                  <select
                    value={folders.find(f => f.id === a.folder_id)?.uuid || ''}
                    onChange={e => handleMoveToFolder(a.uuid, e.target.value)}
                    onClick={e => e.stopPropagation()}
                    title="Move to folder"
                    style={{ fontSize: '10px', border: '1px solid #ddd', borderRadius: '4px', padding: '2px' }}
                  >
                    <option value="">📁</option>
                    {folders.map(f => <option key={f.uuid} value={f.uuid}>{f.name}</option>)}
                  </select>
                )}
              </div>
            </div>
          ));
        })()}
      </aside>

      {/* Main */}
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto', maxWidth: '1100px' }}>
        {comparison ? (
          <ComparisonView data={comparison} onClose={() => { setComparison(null); setSelected(new Set()); }} />
        ) : !active ? (
          <UploadForm
            tab={tab} setTab={setTab}
            url={url} setUrl={setUrl}
            text={text} setText={setText}
            file={file} setFile={setFile}
            jurisdictionLevel={jurisdictionLevel} setJurisdictionLevel={setJurisdictionLevel}
            jurisdictionName={jurisdictionName} setJurisdictionName={setJurisdictionName}
            enforcementAgency={enforcementAgency} setEnforcementAgency={setEnforcementAgency}
            error={error} submitting={submitting}
            onSubmit={handleSubmit}
          />
        ) : loadingDetail ? (
          <p>Loading…</p>
        ) : (
          <AnalysisDetail
            analysis={active}
            onReanalyze={handleReanalyze}
            onDelete={handleDelete}
            onBack={() => { setActive(null); navigate('/regulation-analysis'); }}
          />
        )}
      </main>
    </div>
  );
}

function UploadForm({ tab, setTab, url, setUrl, text, setText, file, setFile,
  jurisdictionLevel, setJurisdictionLevel, jurisdictionName, setJurisdictionName,
  enforcementAgency, setEnforcementAgency, error, submitting, onSubmit }) {
  return (
    <div>
      <h2 style={{ margin: '0 0 6px 0', color: '#1a237e' }}>Analyze a Regulation</h2>
      <p style={{ color: '#666', marginTop: 0, fontSize: '14px' }}>
        Paste a URL, document text, or upload a PDF/DOCX. Tag the jurisdiction so you can filter your library later.
      </p>

      <form onSubmit={onSubmit} style={{
        background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '20px', marginTop: '16px'
      }}>
        {/* Source tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          {['url', 'text', 'file'].map(t => (
            <button type="button" key={t} onClick={() => setTab(t)} style={{
              padding: '6px 14px', borderRadius: '6px', cursor: 'pointer',
              border: tab === t ? '1px solid #1565c0' : '1px solid #ccc',
              background: tab === t ? '#e3f2fd' : 'white',
              color: tab === t ? '#1565c0' : '#555',
              fontSize: '13px', fontWeight: tab === t ? 600 : 400, textTransform: 'capitalize'
            }}>{t}</button>
          ))}
        </div>

        {tab === 'url' && (
          <input type="url" value={url} onChange={e => setUrl(e.target.value)}
            placeholder="https://example.gov/municipal-code/..." style={inputStyle} />
        )}
        {tab === 'text' && (
          <textarea value={text} onChange={e => setText(e.target.value)} rows={10}
            placeholder="Paste regulation text here…" style={{ ...inputStyle, fontFamily: 'inherit' }} />
        )}
        {tab === 'file' && (
          <input type="file" accept=".pdf,.docx,.txt" onChange={e => setFile(e.target.files[0])}
            style={{ ...inputStyle, padding: '8px' }} />
        )}

        {/* Jurisdiction metadata */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px', marginTop: '14px' }}>
          <div>
            <label style={labelStyle}>Jurisdiction level</label>
            <select value={jurisdictionLevel} onChange={e => setJurisdictionLevel(e.target.value)} style={inputStyle}>
              {JURISDICTION_LEVELS.filter(j => j.value).map(j => (
                <option key={j.value} value={j.value}>{j.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Jurisdiction name</label>
            <input type="text" value={jurisdictionName} onChange={e => setJurisdictionName(e.target.value)}
              placeholder="e.g. Indianapolis, IN" style={inputStyle} />
          </div>
        </div>
        <div style={{ marginTop: '12px' }}>
          <label style={labelStyle}>Enforcement agency (optional)</label>
          <input type="text" value={enforcementAgency} onChange={e => setEnforcementAgency(e.target.value)}
            placeholder="e.g. Department of Public Health" style={inputStyle} />
        </div>

        {error && (
          <div style={{ marginTop: '14px', padding: '10px', background: '#fce4ec', color: '#c62828',
            borderRadius: '8px', fontSize: '13px' }}>{error}</div>
        )}

        <button type="submit" disabled={submitting} style={{
          marginTop: '16px', padding: '10px 20px', borderRadius: '8px', border: 'none',
          background: submitting ? '#90caf9' : '#1565c0', color: 'white',
          fontSize: '14px', fontWeight: 600, cursor: submitting ? 'wait' : 'pointer'
        }}>
          {submitting ? 'Submitting…' : 'Analyze regulation'}
        </button>
      </form>
    </div>
  );
}

function QAPanel({ uuid }) {
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState([]); // {q, a}
  const [loading, setLoading] = useState(false);

  const ask = async () => {
    if (!question.trim() || loading) return;
    const q = question.trim();
    setQuestion('');
    setLoading(true);
    const placeholder = { q, a: null };
    setHistory(h => [...h, placeholder]);
    try {
      const res = await fetch(`${API_URL}/api/regulation-analysis/${uuid}/ask`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q })
      });
      const data = await res.json();
      setHistory(h => h.map(item => item === placeholder
        ? { q, a: data.success ? data.answer : `Error: ${data.error || 'Failed to answer'}` }
        : item));
    } catch (err) {
      setHistory(h => h.map(item => item === placeholder ? { q, a: `Error: ${err.message}` } : item));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section title="Ask About This Regulation">
      <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '14px' }}>
        {history.length === 0 && (
          <p style={{ color: '#999', fontStyle: 'italic', fontSize: '13px', margin: 0 }}>
            Ask things like "Does this apply to home-based businesses?" or "What's the deadline for filing the annual report?"
          </p>
        )}
        {history.map((item, i) => (
          <div key={i} style={{ marginBottom: '14px', paddingBottom: '14px', borderBottom: i < history.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
            <div style={{ fontWeight: 600, color: '#1a237e', fontSize: '13px', marginBottom: '6px' }}>Q: {item.q}</div>
            {item.a === null ? (
              <div style={{ color: '#666', fontSize: '13px', fontStyle: 'italic' }}>Thinking…</div>
            ) : (
              <div style={{ fontSize: '13px', color: '#333', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{item.a}</div>
            )}
          </div>
        ))}
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <input type="text" value={question} onChange={e => setQuestion(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') ask(); }}
            placeholder="Ask a question about this regulation…"
            disabled={loading}
            style={{ ...inputStyle, marginTop: 0 }} />
          <button onClick={ask} disabled={loading || !question.trim()} style={{
            padding: '0 16px', borderRadius: '6px', border: 'none',
            background: loading ? '#90caf9' : '#1565c0', color: 'white',
            fontSize: '13px', fontWeight: 600, cursor: loading ? 'wait' : 'pointer'
          }}>{loading ? '…' : 'Ask'}</button>
        </div>
      </div>
    </Section>
  );
}

function AnalysisDetail({ analysis, onReanalyze, onDelete, onBack }) {
  const data = analysis.analysis_data
    ? (typeof analysis.analysis_data === 'string' ? JSON.parse(analysis.analysis_data) : analysis.analysis_data)
    : null;
  const steps = analysis.steps
    ? (typeof analysis.steps === 'string' ? JSON.parse(analysis.steps) : analysis.steps)
    : [];

  return (
    <div>
      <button onClick={onBack} style={{
        background: 'none', border: 'none', color: '#1565c0', cursor: 'pointer',
        fontSize: '13px', padding: 0, marginBottom: '12px'
      }}>← Back to upload</button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: '0 0 4px 0', color: '#1a237e' }}>{analysis.title || 'Regulation Analysis'}</h2>
          <div style={{ fontSize: '13px', color: '#666' }}>
            {analysis.jurisdiction_level && <span style={pill}>{analysis.jurisdiction_level}</span>}
            {analysis.jurisdiction_name && <span style={pill}>{analysis.jurisdiction_name}</span>}
            {analysis.enforcement_agency && <span style={pill}>{analysis.enforcement_agency}</span>}
            {analysis.effective_date && <span style={pill}>Effective {analysis.effective_date}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={onReanalyze} style={btnSecondary}>Reanalyze</button>
          <button onClick={onDelete} style={btnDanger}>Delete</button>
        </div>
      </div>

      {/* Progress */}
      {analysis.status === 'processing' && (
        <div style={{ marginTop: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', color: '#1565c0', fontWeight: 600 }}>{analysis.progress_message}</span>
            <span style={{ fontSize: '13px', color: '#666' }}>{analysis.progress || 0}%</span>
          </div>
          <div style={{ height: '6px', background: '#e0e0e0', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ width: `${analysis.progress || 0}%`, height: '100%', background: '#1565c0', transition: 'width 0.4s' }} />
          </div>
          <ul style={{ marginTop: '12px', paddingLeft: 0, listStyle: 'none', fontSize: '13px' }}>
            {steps.map(s => (
              <li key={s.name} style={{
                color: s.status === 'completed' ? '#2e7d32' :
                       s.status === 'in_progress' ? '#1565c0' :
                       s.status === 'failed' ? '#c62828' : '#999',
                marginBottom: '4px'
              }}>
                {s.status === 'completed' ? '✓' : s.status === 'in_progress' ? '●' : '○'} {s.label}
              </li>
            ))}
          </ul>
        </div>
      )}

      {analysis.status === 'failed' && (
        <div style={{ marginTop: '16px', padding: '14px', background: '#fce4ec', color: '#c62828', borderRadius: '8px' }}>
          <strong>Failed:</strong> {analysis.error_message || 'Unknown error'}
        </div>
      )}

      {data && (
        <div style={{ marginTop: '20px' }}>
          {data.summary && (
            <Section title="Summary">
              <p style={{ lineHeight: 1.6, color: '#333' }}>{data.summary}</p>
            </Section>
          )}

          {Array.isArray(data.requirements) && data.requirements.length > 0 && (
            <Section title={`Compliance Requirements (${data.requirements.length})`}>
              {data.requirements.map((r, i) => (
                <ItemCard key={i}>
                  <div style={{ fontWeight: 600, color: '#1a237e' }}>{r.who}</div>
                  <div style={{ marginTop: '4px' }}>{r.what}</div>
                  {r.deadline_or_frequency && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      ⏱ {r.deadline_or_frequency}
                    </div>
                  )}
                  {r.sourceText && <SourceQuote text={r.sourceText} />}
                </ItemCard>
              ))}
            </Section>
          )}

          {Array.isArray(data.penalties) && data.penalties.length > 0 && (
            <Section title={`Penalties (${data.penalties.length})`}>
              {data.penalties.map((p, i) => (
                <ItemCard key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <div style={{ fontWeight: 600, color: '#1a237e' }}>{p.violation}</div>
                    <span style={{
                      ...pill,
                      background: p.severity === 'high' ? '#fce4ec' : p.severity === 'medium' ? '#fff3e0' : '#e8f5e9',
                      color: p.severity === 'high' ? '#c62828' : p.severity === 'medium' ? '#e65100' : '#2e7d32'
                    }}>{p.severity}</span>
                  </div>
                  <div style={{ marginTop: '4px' }}>{p.penalty}</div>
                  {p.sourceText && <SourceQuote text={p.sourceText} />}
                </ItemCard>
              ))}
              {data.enforcement_process && (
                <div style={{ marginTop: '12px', padding: '12px', background: '#f5f5f5', borderRadius: '8px', fontSize: '13px' }}>
                  <strong>Enforcement process:</strong> {data.enforcement_process}
                </div>
              )}
            </Section>
          )}

          {Array.isArray(data.exemptions) && data.exemptions.length > 0 && (
            <Section title={`Exemptions (${data.exemptions.length})`}>
              {data.exemptions.map((e, i) => (
                <ItemCard key={i}>
                  <div style={{ fontWeight: 600, color: '#1a237e' }}>{e.who}</div>
                  <div style={{ marginTop: '4px' }}>{e.scope}</div>
                  {e.conditions && (
                    <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>
                      <em>Conditions:</em> {e.conditions}
                    </div>
                  )}
                  {e.sourceText && <SourceQuote text={e.sourceText} />}
                </ItemCard>
              ))}
            </Section>
          )}

          {Array.isArray(data.crossReferences) && data.crossReferences.length > 0 && (
            <Section title="Cross-References">
              {data.crossReferences.map((c, i) => (
                <ItemCard key={i}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <code style={{ background: '#eef2ff', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>
                      {c.citation}
                    </code>
                    <span style={pill}>{c.relationship}</span>
                  </div>
                  <div style={{ marginTop: '4px', fontSize: '13px' }}>{c.context}</div>
                </ItemCard>
              ))}
            </Section>
          )}

          <QAPanel uuid={analysis.uuid} />
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginTop: '24px' }}>
      <h3 style={{ color: '#1a237e', borderBottom: '2px solid #e3f2fd', paddingBottom: '6px' }}>{title}</h3>
      <div style={{ marginTop: '12px' }}>{children}</div>
    </section>
  );
}

function ItemCard({ children }) {
  return (
    <div style={{
      background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px',
      padding: '14px', marginBottom: '10px', fontSize: '14px'
    }}>{children}</div>
  );
}

function SourceQuote({ text }) {
  return (
    <blockquote style={{
      margin: '8px 0 0 0', padding: '8px 12px', borderLeft: '3px solid #1565c0',
      background: '#f5f9ff', fontSize: '12px', color: '#444', fontStyle: 'italic'
    }}>"{text}"</blockquote>
  );
}

function ComparisonView({ data, onClose }) {
  const c = data.comparison || {};
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, color: '#1a237e' }}>Cross-Jurisdiction Comparison</h2>
        <button onClick={onClose} style={btnSecondary}>Close</button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
        {(data.regulations || []).map(r => (
          <span key={r.uuid} style={{ ...pill, background: '#e3f2fd', color: '#1565c0', padding: '6px 12px' }}>
            <strong>{r.title}</strong> · {r.jurisdiction_level} {r.jurisdiction_name}
          </span>
        ))}
      </div>

      {c.rawAnalysis ? (
        <pre style={{ background: '#f5f5f5', padding: '14px', borderRadius: '8px', whiteSpace: 'pre-wrap', fontSize: '13px' }}>{c.rawAnalysis}</pre>
      ) : (
        <>
          {c.summary && (
            <Section title="Takeaway">
              <p style={{ lineHeight: 1.6 }}>{c.summary}</p>
            </Section>
          )}

          {Array.isArray(c.strictness_ranking) && c.strictness_ranking.length > 0 && (
            <Section title="Strictness Ranking">
              {c.strictness_ranking.sort((a, b) => (a.rank || 0) - (b.rank || 0)).map((r, i) => (
                <ItemCard key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong>#{r.rank} · {r.jurisdiction}</strong>
                  </div>
                  <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>{r.reasoning}</div>
                </ItemCard>
              ))}
            </Section>
          )}

          {Array.isArray(c.common_requirements) && c.common_requirements.length > 0 && (
            <Section title={`Common Requirements (${c.common_requirements.length})`}>
              {c.common_requirements.map((r, i) => (
                <ItemCard key={i}>
                  <div>{r.what}</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Applies to: {(r.appliesTo || []).join(', ')}
                  </div>
                </ItemCard>
              ))}
            </Section>
          )}

          {Array.isArray(c.unique_requirements) && c.unique_requirements.length > 0 && (
            <Section title={`Jurisdiction-Specific Requirements (${c.unique_requirements.length})`}>
              {c.unique_requirements.map((r, i) => (
                <ItemCard key={i}>
                  <div style={{ fontWeight: 600, color: '#1a237e' }}>{r.jurisdiction}</div>
                  <div style={{ marginTop: '4px' }}>{r.what}</div>
                  {r.why_distinctive && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px', fontStyle: 'italic' }}>
                      Why distinctive: {r.why_distinctive}
                    </div>
                  )}
                </ItemCard>
              ))}
            </Section>
          )}

          {Array.isArray(c.penalty_differences) && c.penalty_differences.length > 0 && (
            <Section title="Penalty Differences">
              {c.penalty_differences.map((p, i) => (
                <ItemCard key={i}>
                  <div style={{ fontWeight: 600, color: '#1a237e', marginBottom: '6px' }}>{p.topic}</div>
                  {(p.byJurisdiction || []).map((j, k) => (
                    <div key={k} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '4px' }}>
                      <span style={{ ...pill, fontSize: '11px' }}>{j.jurisdiction}</span>
                      <span style={{
                        ...pill,
                        background: j.severity === 'high' ? '#fce4ec' : j.severity === 'medium' ? '#fff3e0' : '#e8f5e9',
                        color: j.severity === 'high' ? '#c62828' : j.severity === 'medium' ? '#e65100' : '#2e7d32',
                        fontSize: '11px'
                      }}>{j.severity}</span>
                      <span style={{ fontSize: '13px' }}>{j.penalty}</span>
                    </div>
                  ))}
                </ItemCard>
              ))}
            </Section>
          )}
        </>
      )}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '10px', borderRadius: '6px',
  border: '1px solid #ccc', fontSize: '14px', boxSizing: 'border-box'
};
const labelStyle = {
  display: 'block', fontSize: '12px', color: '#555', marginBottom: '4px', fontWeight: 600
};
const pill = {
  display: 'inline-block', padding: '2px 8px', borderRadius: '12px',
  background: '#e3f2fd', color: '#1565c0', fontSize: '12px', marginRight: '6px'
};
const btnSecondary = {
  padding: '6px 14px', borderRadius: '6px', border: '1px solid #90caf9',
  background: 'white', color: '#1565c0', cursor: 'pointer', fontSize: '13px'
};
const btnDanger = {
  padding: '6px 14px', borderRadius: '6px', border: '1px solid #ef9a9a',
  background: 'white', color: '#c62828', cursor: 'pointer', fontSize: '13px'
};

export default RegulationAnalysis;
